import { Button } from '@/components/ui/button';
import AllPartPOTable from '@/js/components/Tables/AllPartPOTable';
import AllPartsContractTable from '@/js/components/Tables/AllPartsContractTable';
import AllPartsQuotationsTable from '@/js/components/Tables/AllPartsQuotationsTable';
import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import PartQuotesDashboardScreen from './AllParts/PartQuotesDashboardScreen';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import createApiClient from '@/api/axiosInstance';
import CreateContractScreen from './AllParts/CreateContractScreen';
import ContractStatusScreen from './AllParts/ContractStatusScreen';
import CreatePOScreen from './AllParts/CreatePOScreen';
import SendPOScreen from './AllParts/SendPOScreen';
import { Input } from '@/components/ui/input';

const screenEnum = {
  ALL_PARTS: 'ALL_PARTS',
  PART_QUOTE_DASHBOARD: 'PART_QUOTE_DASHBOARD',
  CREATE_CONTRACT: 'CREATE_CONTRACT',
  CONTRACT_STATUS: 'CONTRACT_STATUS',
  CREATE_PO: 'CREATE_PO',
  SEND_PO: 'SEND_PO',
};

const tableScreen = {
  QUOTATIONS: 'Quotations',
  CONTRACT: 'Contract',
  PURCHASE_ORDER: 'Purchase_Order',
};

const AllPartsScreenCode = ({
  setScreen,
  setPart,
  setContract,
  setPO,
  field,
  setField,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apiClient = createApiClient(navigate, dispatch);
  const partsData = useSelector((state) => state?.projectReducer?.parts);
  const { projectId, projectName } = useParams();

  const [quoteTableData, setQuoteTableData] = useState([]);
  const [contractTableData, setContractTableData] = useState([]);
  const [POTableData, setPOTableData] = useState([]);
  const [checkedParts, setCheckedParts] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const getQuoteTableData = async () => {
      try {
        const response = await apiClient.post(
          '/api/quotation/project-id',
          JSON.stringify({
            project_id: Number(projectId),
          }),
        );

        const responseData = response?.data;
        setQuoteTableData(responseData.data);
      } catch (error) {
        console.error('ERROR IN GETTING SCREEN DATA');
      }
    };

    const getContractTableData = async (partId) => {
      try {
        const response = await apiClient.post(
          '/api/quotation/project-part-id',
          JSON.stringify({
            project_id: Number(projectId),
            part_id: partId,
          }),
        );

        const responseData = response?.data;
        return responseData;
      } catch (error) {
        console.error('ERROR IN GETTING SCREEN DATA');
      }
    };

    getQuoteTableData();

    if (partsData?.length > 0) {
      Promise.all(
        partsData.map((part) => getContractTableData(part.part_id)),
      ).then((responses) => {
        const allData = responses
          .map((res) => res?.data)
          .filter(Boolean)
          .flat();

        setContractTableData(allData);
        setPOTableData(allData);
      });
    }
  }, []);

  const getTableComponent = () => {
    switch (field) {
      case tableScreen.QUOTATIONS:
        return (
          <AllPartsQuotationsTable
            key="Quotations"
            setPart={setPart}
            setScreen={setScreen}
            tableData={quoteTableData}
            checkedParts={checkedParts}
            setCheckedParts={setCheckedParts}
            searchTerm={searchTerm}
          />
        );
      case tableScreen.CONTRACT:
        return (
          <AllPartsContractTable
            key="Contract"
            setScreen={setScreen}
            setContract={setContract}
            tableData={contractTableData}
          />
        );
      case tableScreen.PURCHASE_ORDER:
        return (
          <AllPartPOTable
            key="Purchase_Order"
            setScreen={setScreen}
            setPO={setPO}
            tableData={POTableData}
          />
        );
      default:
        return null;
    }
  };

  const handleSendRfqClick = () => {
    navigate(`/project/createRFQ/${projectName}/${projectId}`, {
      state: {
        partsSelected: checkedParts,
      },
    });
  };

  return (
    <div className="py-8">
      <div className="flex justify-between items-center mb-6 h-[24px]">
        <h1 className="text-lg font-medium">All Parts</h1>
        {field === tableScreen.QUOTATIONS && (
          <div className="flex gap-4 items-center">
            {/* Search Input */}
            <Input
              type="text"
              placeholder="Search Part"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-[300px] focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 disabled:border disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:text-black disabled:bg-white"
            />

            {/* Compare Button */}
            <Button
              variant="default"
              onClick={handleSendRfqClick}
              disabled={checkedParts.length < 1}
              className={`px-4 py-2 text-sm rounded-md  transition-all ease-in duration-300 ${
                checkedParts.length < 1
                  ? 'text-gray-600 bg-gray-200 border-gray-300 cursor-not-allowed'
                  : 'bg-darkPurple text-white hover:bg-darkerPurple'
              }`}
            >
              Send RFQ
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-8 p-5 pb-0 pl-2 items-center font-medium text-sm text-center border-b border-gray-300 hover:cursor-pointer">
        {Object.entries(tableScreen).map(([key, value]) => (
          <div
            key={value}
            className={`w-32 h-6 transition-all ease-in duration-300 ${
              field === value
                ? 'text-black border-b-2 border-black scale-[115%]'
                : 'text-gray-400'
            }`}
            onClick={() => setField(value)}
          >
            {value.replace('_', ' ')}
          </div>
        ))}
      </div>

      <div className="mt-2 relative min-h-[200px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={field}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {getTableComponent()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

const AllPartsScreen = () => {
  const [field, setField] = useState(tableScreen.QUOTATIONS);
  const [screen, setScreen] = useState(screenEnum.ALL_PARTS);

  const [part, setPart] = useState(null);
  const [contract, setContract] = useState(null);
  const [PO, setPO] = useState(null);

  const getScreen = () => {
    switch (screen) {
      case screenEnum.ALL_PARTS:
        return (
          <AllPartsScreenCode
            field={field}
            setField={setField}
            setScreen={setScreen}
            setPart={setPart}
            setContract={setContract}
            setPO={setPO}
          />
        );
      case screenEnum.PART_QUOTE_DASHBOARD:
        return (
          <PartQuotesDashboardScreen
            setScreen={setScreen}
            part={part}
            setPart={setPart}
          />
        );

      case screenEnum.CREATE_CONTRACT:
        return (
          <CreateContractScreen
            setScreen={setScreen}
            contract={contract}
            setContract={setContract}
            setField={setField}
          />
        );

      case screenEnum.CONTRACT_STATUS:
        return (
          <ContractStatusScreen
            setScreen={setScreen}
            contract={contract}
            setContract={setContract}
            setField={setField}
          />
        );
      case screenEnum.CREATE_PO:
        return (
          <CreatePOScreen
            setScreen={setScreen}
            PO={PO}
            setPO={setPO}
            setField={setField}
          />
        );
      case screenEnum.SEND_PO:
        return (
          <SendPOScreen
            setScreen={setScreen}
            PO={PO}
            setPO={setPO}
            setField={setField}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="bg-white rounded-lg font-roboto overflow-y-auto"
      style={{
        scrollbarWidth: 'none',
      }}
    >
      <div className="px-10 h-[calc(100vh-60px)]">
        <AnimatePresence mode="wait">
          <motion.div
            key={screen}
            initial={{ x: 100, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -100, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            {getScreen()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AllPartsScreen;
